import { z } from 'zod'
import { jobTypeSchema, type JobType } from '../enums/job-type.enum.js'
import { proposalWorkSchema } from '../enums/proposal-work.enum.js'

/**
 * A section of authored prose: an optional heading, paragraphs, bullets.
 *
 * The API's proposal templates import this type (as `ProseCopy`) so the
 * wording they ship and the wording Settings edits stay the same shape.
 */
export const proseCopySchema = z.object({
  title: z.string().trim().max(200).nullable(),
  paragraphs: z.array(z.string().trim().min(1).max(4000)).max(30),
  bullets: z.array(z.string().trim().min(1).max(1000)).max(40),
})

export type ProseCopyDto = z.infer<typeof proseCopySchema>

/** The signature block's wording, which is not shaped like the prose sections. */
export const acceptanceCopySchema = z.object({
  signerLabel: z.string().trim().min(1).max(200),
  requiresBoardApproval: z.boolean(),
  note: z.string().trim().max(4000).nullable(),
})

/**
 * The prose sections that can be rewritten, in the order they are edited.
 *
 * A fixed list rather than free-form keys: each one is rendered by a block that
 * knows where it goes, so a section nobody can render is not something to let
 * somebody create.
 */
export const EDITABLE_PROSE_SECTIONS = [
  'narrative',
  'exclusions',
  'access',
  'preparation',
  'standards',
  'terms',
  'comparison',
  'assurances',
  'nextSteps',
  'reviews',
  'sinceLastVisit',
  'futureWork',
] as const

export type EditableProseSection = (typeof EDITABLE_PROSE_SECTIONS)[number]

/**
 * Every key `sources` records: a prose section, the acceptance block, or one
 * job type's description.
 *
 * A fixed list, like the sections themselves. The editor asks for a source by
 * one of these keys, and a key that is only a `string` would let a typo read
 * as "no source" instead of failing to compile.
 */
type ExpectedTemplateSourceKey =
  | EditableProseSection
  | 'acceptance'
  | `jobTypeDescriptions.${JobType}`

export const TEMPLATE_SOURCE_KEYS = [
  'narrative',
  'exclusions',
  'access',
  'preparation',
  'standards',
  'terms',
  'comparison',
  'assurances',
  'nextSteps',
  'reviews',
  'sinceLastVisit',
  'futureWork',
  'acceptance',
  'jobTypeDescriptions.CROWN_CLEANING',
  'jobTypeDescriptions.CROWN_RAISE',
  'jobTypeDescriptions.CROWN_RESTORATION',
  'jobTypeDescriptions.CROWN_THIN',
  'jobTypeDescriptions.STRUCTURAL_CLEARANCE',
  'jobTypeDescriptions.OTHER',
] as const satisfies readonly ExpectedTemplateSourceKey[]

type MissingTemplateSourceKey = Exclude<
  ExpectedTemplateSourceKey,
  (typeof TEMPLATE_SOURCE_KEYS)[number]
>

function assertTemplateSourceKeys(
  covered: MissingTemplateSourceKey extends never ? true : never,
): true {
  return covered
}

assertTemplateSourceKeys(true)

export const templateSourceKeySchema = z.enum(TEMPLATE_SOURCE_KEYS)

export type TemplateSourceKey = z.infer<typeof templateSourceKeySchema>

/** What each section is called where somebody edits it. */
export const PROSE_SECTION_LABELS: Record<EditableProseSection, string> = {
  narrative: 'Opening narrative',
  exclusions: 'What is not included',
  access: 'Access, scheduling and residents',
  preparation: 'Site preparation and safety',
  standards: 'How the work is performed',
  terms: 'Terms',
  comparison: 'Questions to ask every bidder',
  assurances: 'Beside the price',
  nextSteps: 'What happens after you accept',
  reviews: 'Reviews',
  sinceLastVisit: 'Since our last visit',
  futureWork: 'Future work',
}

/** What each section is for, so nobody has to infer it from the default text. */
export const PROSE_SECTION_HINTS: Record<EditableProseSection, string> = {
  narrative:
    'Opens the proposal, after the summary. Says why this work and why now.',
  exclusions:
    'What the price does not cover. Read closely at invoice time, so it is worth being exact.',
  access:
    'What happens on the property — the part a community manager has to manage.',
  preparation:
    'What the crew does around the work to keep people and property out of harm’s way — the part a board asks about when it is thinking about liability.',
  standards:
    'How the work is done, and to whose standard. This is where a board looks for competence.',
  terms:
    'Payment, scheduling, cancellation, and what happens if the work turns out different from what was quoted.',
  comparison:
    'Sits before the price, for a board comparing bids. Write each bullet as a question and its answer: "Is a deposit required? No." The page splits them at the first question mark.',
  assurances:
    'The terms that take the risk out of saying yes, shown beside the total. One bullet each, a short heading then a dash and the detail: "No deposit — Nothing to pay up front." Keep them true to the Terms below.',
  nextSteps:
    'The steps after a yes, shown beside the Accept button. One bullet per step, in order. Promise only what the crew actually does.',
  reviews:
    'Customer reviews, quoted exactly. One bullet each, ending with the attribution after a dash: "They cleaned up before they left. — Steve P. · Google". Leave it empty to show none.',
  sinceLastVisit:
    'Heads what this visit found again and what changed on the property since the last one, under What we found. Shown only for a property visited before. The lines under it are written from the survey, so this is the heading and an opening sentence.',
  futureWork:
    'Heads the work recommended for a later season, after the price. Shown only when some work is. The paragraphs are the note each estimate\'s asterisk points to, so say plainly that these are estimates, rebid when the season comes, and not approved with this proposal.',
}

/**
 * Wording that overrides what is written in code.
 *
 * Every field optional, and that is the whole design: this stores only what
 * somebody has CHANGED. An absent section means "use the level below", so an
 * empty record behaves exactly like the app did before any of this existed,
 * and the defaults stay in code where they are reviewed and versioned.
 *
 * It also means there is nothing to migrate and nothing to seed. A deployment
 * that has never opened the editor renders the same proposals it always did.
 */
export const proposalTemplateOverridesSchema = z.object({
  narrative: proseCopySchema.optional(),
  exclusions: proseCopySchema.optional(),
  access: proseCopySchema.optional(),
  preparation: proseCopySchema.optional(),
  standards: proseCopySchema.optional(),
  terms: proseCopySchema.optional(),
  comparison: proseCopySchema.optional(),
  assurances: proseCopySchema.optional(),
  nextSteps: proseCopySchema.optional(),
  reviews: proseCopySchema.optional(),
  sinceLastVisit: proseCopySchema.optional(),
  futureWork: proseCopySchema.optional(),
  acceptance: acceptanceCopySchema.optional(),
  /**
   * What each kind of work means, in a client's language.
   *
   * Partial on purpose: rewriting one job type must not require restating the
   * other four, and a job type added later falls back to its code default
   * rather than rendering blank.
   *
   * `partialRecord`, NOT `record`. A Zod record keyed by an enum is
   * EXHAUSTIVE — it demands every member — so `z.record(jobTypeSchema, ...)`
   * rejects a patch that rewrites one job type with "expected string, received
   * undefined" for the other four. Found by sending exactly that.
   */
  jobTypeDescriptions: z
    .partialRecord(jobTypeSchema, z.string().trim().max(4000))
    .optional(),
})

export type ProposalTemplateOverrides = z.infer<
  typeof proposalTemplateOverridesSchema
>

/**
 * The wording as it will actually render, with every level already applied.
 *
 * Resolved rather than raw, because an editor showing "nothing set" for a
 * section a client will nevertheless read is an editor that hides the thing it
 * is for. What it does NOT convey is which level a value came from — that is
 * `source`, alongside.
 */
export const resolvedTemplateSchema = z.object({
  narrative: proseCopySchema,
  exclusions: proseCopySchema,
  access: proseCopySchema,
  preparation: proseCopySchema,
  standards: proseCopySchema,
  terms: proseCopySchema,
  comparison: proseCopySchema,
  assurances: proseCopySchema,
  nextSteps: proseCopySchema,
  reviews: proseCopySchema,
  sinceLastVisit: proseCopySchema,
  futureWork: proseCopySchema,
  acceptance: acceptanceCopySchema,
  /** Exhaustive here, unlike the overrides: every job type must render. */
  jobTypeDescriptions: z.record(jobTypeSchema, z.string()),
})

export type ResolvedTemplateDto = z.infer<typeof resolvedTemplateSchema>

/**
 * Where each section's wording came from.
 *
 * Shown beside the editor so somebody can tell "this is the standard wording"
 * from "somebody changed this for this proposal" — a distinction that decides
 * whether editing here affects one client or every future one.
 */
export const templateSourceSchema = z.enum(['default', 'organization', 'proposal'])

export type TemplateSource = z.infer<typeof templateSourceSchema>

/**
 * Which level supplied each section.
 *
 * Exhaustive on purpose: resolution always records a source for every key,
 * and a missing key would otherwise look like "still the default".
 */
export const templateSourcesSchema = z.record(
  templateSourceKeySchema,
  templateSourceSchema,
)

export type TemplateSources = z.infer<typeof templateSourcesSchema>

export const proposalTemplateDtoSchema = z.object({
  /**
   * Which wording this is: for trees, irrigation, or both. The business keeps
   * one set for each, because a proposal says what it bids; one proposal's
   * wording is layered over the set for what it bids.
   */
  work: proposalWorkSchema,
  /** Everything as it renders, defaults included. */
  resolved: resolvedTemplateSchema,
  /**
   * What this level would render if it overrode nothing.
   *
   * The floor under the editor, and what Reset reveals. `resolved` cannot
   * serve that purpose: it already has this level's overrides applied, so
   * falling back to it after dropping one shows the value just discarded and
   * makes Reset look like it did nothing.
   */
  inherited: resolvedTemplateSchema,
  /** Only what has been overridden at this level. */
  overrides: proposalTemplateOverridesSchema,
  /** Per section, which level supplied the wording. */
  sources: templateSourcesSchema,
  /**
   * The same, for `inherited` — where a section would come FROM if this level
   * stopped overriding it. Without this the tag beside a section Reset but not
   * yet saved would still claim this level owns it.
   */
  inheritedSources: templateSourcesSchema,
})

export type ProposalTemplateDto = z.infer<typeof proposalTemplateDtoSchema>
